import { mediacoreApi } from '@apis/mediacoreApi'
import {
  createOneMutation,
  deleteByIdMutation,
  getAllByClientIdQuery,
  getByIdAndClientIdQuery,
  pkSubEndpointMutation,
  updateByIdMutation,
} from '@apis/mediacoreQueries'
import { GalleryPayload, Resource } from '@types'

const marketingTasksUrl = '/marketing/tareas'

const marketingTasksApi = mediacoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getTaskById: builder.query<Resource, { id: number; clientId: number }>({
      query: getByIdAndClientIdQuery(marketingTasksUrl),
      providesTags: (_result, _error, { id }) => [{ type: 'MarketingTask', id }],
    }),
    getTasks: builder.query<Resource[], { clientId: number }>({
      query: getAllByClientIdQuery(marketingTasksUrl),
      providesTags: ['MarketingTask'],
    }),
    createTask: builder.mutation<Resource, { data: Partial<Resource> }>({
      query: createOneMutation(marketingTasksUrl),
      invalidatesTags: ['MarketingTaskTable', 'MarketingObjective'],
    }),
    deleteTask: builder.mutation<void, { id: number; clientId: number }>({
      query: deleteByIdMutation(marketingTasksUrl),
      invalidatesTags: ['MarketingTaskTable', 'MarketingObjective'],
    }),
    updateTask: builder.mutation<Resource, { id: number; data: Partial<Resource> }>({
      query: updateByIdMutation(marketingTasksUrl),
      invalidatesTags: ['MarketingTask', 'MarketingTaskTable', 'MarketingTaskEvent', 'MarketingObjective'],
    }),
    uploadTaskFiles: builder.mutation<unknown, { id: number; data: GalleryPayload }>({
      query: pkSubEndpointMutation(marketingTasksUrl, 'galeria_archivos'),
      invalidatesTags: ['MarketingTask', 'MarketingTaskTable', 'MarketingTaskEvent'],
    }),
    validateTaskStatus: builder.query<{ is_valid: boolean }, { id: number; clientId: number }>({
      query: getByIdAndClientIdQuery(marketingTasksUrl, 'validar-status'),
      providesTags: (_result, _error, { id }) => [{ type: 'MarketingTask', id }],
    }),
    getAvailableTasks: builder.query<Resource[], { clientId: number; pk?: number }>({
      query: ({ clientId, pk }) => ({
        url: `${marketingTasksUrl}/available/`,
        method: 'GET',
        params: { client: clientId, pk },
      }),
      providesTags: ['MarketingTask', 'MarketingTaskTable', 'MarketingTaskEvent'],
    }),
    uploadTaskImages: builder.mutation<unknown, { id: number; data: GalleryPayload }>({
      query: pkSubEndpointMutation(marketingTasksUrl, 'galeria'),
      invalidatesTags: ['MarketingTask', 'MarketingTaskTable', 'MarketingTaskEvent'],
    }),
    reorderTaskOrder: builder.mutation<
      unknown,
      { clientId: number; ordered: Array<{ id: number; order: number; parent?: number | null }> }
    >({
      query: ({ clientId, ordered }) => ({
        url: `${marketingTasksUrl}/reorder/`,
        method: 'POST',
        body: {
          client: clientId,
          ordered,
        },
      }),
      invalidatesTags: ['MarketingTask', 'MarketingTaskTable', 'MarketingObjective'],
    }),
  }),
  overrideExisting: false,
})
export const {
  useGetTaskByIdQuery,
  useLazyGetTaskByIdQuery,
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useUploadTaskFilesMutation,
  useDeleteTaskMutation,
  useValidateTaskStatusQuery,
  useLazyValidateTaskStatusQuery,
  useGetAvailableTasksQuery,
  useUploadTaskImagesMutation,
  useReorderTaskOrderMutation,
} = marketingTasksApi
